import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  FileText, 
  Plus, 
  User,
  Menu,
  X,
  LayoutDashboard,
  ShoppingCart,
  Palette,
  LogOut,
  Bell,
  DollarSign
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import DesignCaseNotifications from './DesignCaseNotifications';

const SellerLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const menuItems = [
    {
      name: 'Dashboard',
      icon: LayoutDashboard,
      path: '/seller'
    },
    {
      name: 'Quản lý đơn hàng',
      icon: ShoppingCart,
      path: '/seller/orders'
    },
    {
      name: 'Design Cases',
      icon: Palette,
      path: '/seller/design-cases'
    },
    {
      name: 'Quản lý tài chính',
      icon: DollarSign,
      path: '/seller/finance'
    },
    {
      name: 'Thông báo',
      icon: Bell,
      path: '/seller/notifications',
      isNotification: true
    },
    {
      name: 'Thông tin cá nhân',
      icon: User,
      path: '/seller/profile'
    }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile menu overlay */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}
        onClick={toggleMobileMenu}
      />
      
      {/* Sidebar */}
      <nav className={`fixed inset-y-0 left-0 w-64 bg-white shadow-lg z-40 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300 ease-in-out lg:static lg:shadow-none lg:bg-transparent`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 lg:border-none">
          <h1 className="text-xl font-bold text-gray-900">DVT HULO SYSTEM Seller</h1>
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4">
          <div className="mb-6">
            <div className="flex items-center space-x-3">
              <img
                src={user?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'}
                alt={user?.name}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.name || 'Seller'}</p>
                <p className="text-xs text-gray-500">Seller Account</p>
              </div>
            </div>
          </div>
          
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              if (item.isNotification) {
                return (
                  <div key={item.name} className="relative">
                    <DesignCaseNotifications />
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {Icon && <Icon className="w-5 h-5" />}
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>
          
          {/* Logout Button */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => {
                sessionStorage.removeItem('token');
                window.location.href = '/login';
              }}
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors w-full"
            >
              <LogOut className="w-5 h-5" />
              <span>Đăng xuất</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between p-4">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">DVT HULO SYSTEM Seller</h1>
            <div className="w-10"></div> {/* Spacer for centering */}
          </div>
        </div>

        {/* Content area */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default SellerLayout; 
